import type { Driver, RivalDef, Team } from "../types";
import { Rng } from "./rng";
import { findDriver } from "../data/drivers";
import { findChassis } from "../data/chassis";
import { findDirector } from "../data/directors";
import { MIDFIELD_TEAMS, RIVAL_TEAMS } from "../data/rivals";
import { makeTeam } from "./team";

// Of the GRID_RIVALS opponents, how many are drawn from the elite RIVAL_TEAMS;
// the rest come from MIDFIELD_TEAMS. An all-elite grid made any sub-elite player
// team a near-0% title shot (see scripts/sim-grid.ts); ~3 elite + the rest
// midfield gives a mid-strength draft a real chance without trivialising it.
const ELITE_IN_GRID = 3;

function buildTeam(def: RivalDef): Team {
  const d1 = findDriver(def.d1);
  const d2 = findDriver(def.d2);
  const chassis = findChassis(def.chassis);
  const director = findDirector(def.director);
  if (!d1 || !d2 || !chassis || !director) {
    // A misconfigured roster entry is a developer bug — surface it loudly.
    throw new Error(`Unresolved rival lineup: ${def.name}`);
  }
  return makeTeam({ name: def.name, d1, d2, chassis, director, isPlayer: false });
}

/** Build all resolvable ELITE rival teams from the fixed roster. */
export function allRivals(): Team[] {
  return RIVAL_TEAMS.map(buildTeam);
}

/** Build all resolvable MIDFIELD rival teams. */
export function allMidfieldRivals(): Team[] {
  return MIDFIELD_TEAMS.map(buildTeam);
}

const driverKey = (d: Driver): string => `${d.name}|${d.team}`;

/**
 * Draw up to `want` teams from `pool` into `out`, skipping any that would reuse
 * a chassis model or driver card already taken. Stops at `want` or when the grid
 * reaches `n`. Mutates the used-sets and `out`.
 */
function drawInto(
  rng: Rng,
  pool: readonly Team[],
  want: number,
  n: number,
  usedDrivers: Set<string>,
  usedChassis: Set<string>,
  out: Team[],
): void {
  let added = 0;
  for (const team of rng.shuffle(pool)) {
    if (out.length >= n || added >= want) break;
    const k1 = driverKey(team.d1);
    const k2 = driverKey(team.d2);
    if (usedChassis.has(team.chassis.model) || usedDrivers.has(k1) || usedDrivers.has(k2)) {
      continue;
    }
    usedDrivers.add(k1);
    usedDrivers.add(k2);
    usedChassis.add(team.chassis.model);
    out.push(team);
    added++;
  }
}

/**
 * Draw n rival lineups as a mix of elite and midfield teams, never fielding the
 * same driver card (name + season) or chassis on two rival teams. The midfield
 * is drawn FIRST so its quota is guaranteed (its driver cards are disjoint from
 * the elite roster, so it never starves the elite draw); the elite slots then
 * fill from the remaining non-colliding lineups, and a final backfill from both
 * pools tops up to n if dedup ran either short. Rivals MAY reuse the player's
 * own drivers — that's intentional.
 */
export function buildRivals(rng: Rng, n: number): Team[] {
  const usedDrivers = new Set<string>();
  const usedChassis = new Set<string>();
  const out: Team[] = [];
  const eliteWanted = Math.min(ELITE_IN_GRID, n);
  drawInto(rng, allMidfieldRivals(), n - eliteWanted, n, usedDrivers, usedChassis, out);
  drawInto(rng, allRivals(), eliteWanted, n, usedDrivers, usedChassis, out);
  // Backfill from both pools in case dedup left either short of its quota.
  drawInto(rng, allRivals(), n, n, usedDrivers, usedChassis, out);
  drawInto(rng, allMidfieldRivals(), n, n, usedDrivers, usedChassis, out);
  return out;
}
